import { useState, useEffect } from 'react';
import { Pencil, Power, Check, X as XIcon, Plus, Lock, History, Info } from 'lucide-react';
import { configAPI, userAPI, roleAPI } from '@/services/api';
import FactoryPnLConfigPage from './FactoryPnLConfigPage';
import CycleTimesTab from './admin/CycleTimesTab';
import { useAuth } from '@/hooks/useAuth';
import { t } from '@/i18n/translations';
import { formatApiError } from '@/lib/apiError';
import { toast } from '@/components/ui/sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const PERMISSION_KEYS = [
  'rfq_view_own',
  'rfq_view_all',
  'rfq_create',
  'rfq_edit_own',
  'rfq_edit_all',
  'rfq_submit',
  'rfq_approve',
  'rfq_reject',
  'rfq_view_margin',
  'rfq_assign_engineer',
  'doc_attach',
  'doc_download',
  'offer_generate',
  'offer_edit',
  'offer_download',
  'client_view',
  'client_create',
  'client_edit',
  'client_financial_edit',
  'history_view',
  'history_add',
];

function errorMessage(err, fallback) {
  return formatApiError(err, fallback);
}

export default function AdminPage() {
  const { lang, user } = useAuth();
  const [tab, setTab] = useState('machines');
  const [machines, setMachines] = useState([]);
  const [alloys, setAlloys] = useState([]);
  const [finishingRates, setFinishingRates] = useState([]);
  const [boxTypes, setBoxTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [activitySectors, setActivitySectors] = useState([]);
  const [contactFunctions, setContactFunctions] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const reloadAll = () => {
    Promise.all([
      configAPI.getMachines(),
      configAPI.getAlloys(),
      configAPI.getFinishingRates(true), // include_inactive: o admin vê todas
      configAPI.getBoxTypes(true), // include_inactive: o admin vê todas
      configAPI.getSuppliers(true), // include_inactive: o admin vê todos
      configAPI.getActivitySectors(true),
      configAPI.getContactFunctions(true),
      userAPI.list(),
      roleAPI.list(),
    ])
      .then(([m, a, f, b, sp, sec, fn, u, r]) => {
        setMachines(m.data);
        setAlloys(a.data);
        setFinishingRates(f.data);
        setBoxTypes(b.data);
        setSuppliers(sp.data);
        setActivitySectors(sec.data);
        setContactFunctions(fn.data);
        setUsers(u.data);
        setRoles(r.data);
      })
      .catch(() => toast.error(t('load_error', lang)))
      .finally(() => setLoading(false));
  };

  // FE-02: só administradores carregam dados sensíveis (utilizadores, roles,
  // catálogos). Sem este guard, um não-admin que chegasse a /admin dispararia
  // userAPI.list()/configAPI.* (rejeitados com 403, mas expõem a tentativa e
  // facilitam reconhecimento). O guard de render abaixo trata da UI.
  useEffect(() => {
    if (user?.is_admin) {
      reloadAll();
    } else {
      setLoading(false);
    }
  }, [user?.is_admin]);

  if (!user?.is_admin) {
    return (
      <Alert variant="destructive">
        <Lock className="h-4 w-4" />
        <AlertTitle>{t('access_restricted', lang)}</AlertTitle>
        <AlertDescription>{t('admin_only_desc', lang)}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-7">
      <PageHeader kicker={t('config', lang)} title={t('admin', lang)} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto min-h-10 w-full flex-wrap justify-start">
          <TabsTrigger value="machines">{t('machines', lang)}</TabsTrigger>
          <TabsTrigger value="alloys">{t('alloys', lang)}</TabsTrigger>
          <TabsTrigger value="finishing">{t('finishing_rates', lang)}</TabsTrigger>
          <TabsTrigger value="boxes">{t('boxes_admin_tab', lang)}</TabsTrigger>
          <TabsTrigger value="suppliers">{t('suppliers_admin_tab', lang)}</TabsTrigger>
          <TabsTrigger value="sectors">{t('activity_sectors_tab', lang)}</TabsTrigger>
          <TabsTrigger value="functions">{t('contact_functions_tab', lang)}</TabsTrigger>
          <TabsTrigger value="cycletimes">{t('cycle_times_tab', lang)}</TabsTrigger>
          <TabsTrigger value="users">{t('users', lang)}</TabsTrigger>
          <TabsTrigger value="roles">{t('roles', lang)}</TabsTrigger>
          <TabsTrigger value="pnl">{t('pnl_tab_label', lang)}</TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="mt-4 space-y-2">
            <Skeleton className="h-32" />
            <Skeleton className="h-40" />
          </div>
        ) : (
          <>
            <TabsContent value="machines" className="mt-6">
              <MachinesTab lang={lang} machines={machines} setMachines={setMachines} />
            </TabsContent>
            <TabsContent value="alloys" className="mt-6">
              <AlloysTab lang={lang} alloys={alloys} setAlloys={setAlloys} />
            </TabsContent>
            <TabsContent value="finishing" className="mt-6">
              <FinishingTab
                lang={lang}
                rates={finishingRates}
                setRates={setFinishingRates}
              />
            </TabsContent>
            <TabsContent value="boxes" className="mt-6">
              <BoxTypesTab lang={lang} boxTypes={boxTypes} setBoxTypes={setBoxTypes} />
            </TabsContent>
            <TabsContent value="suppliers" className="mt-6">
              <SuppliersTab lang={lang} suppliers={suppliers} setSuppliers={setSuppliers} />
            </TabsContent>
            <TabsContent value="sectors" className="mt-6">
              <SimpleCatalogTab
                lang={lang}
                items={activitySectors}
                setItems={setActivitySectors}
                titleKey="activity_sectors_title"
                descKey="activity_sectors_desc"
                newLabelKey="new_activity_sector"
                create={configAPI.createActivitySector}
                update={configAPI.updateActivitySector}
              />
            </TabsContent>
            <TabsContent value="functions" className="mt-6">
              <SimpleCatalogTab
                lang={lang}
                items={contactFunctions}
                setItems={setContactFunctions}
                titleKey="contact_functions_title"
                descKey="contact_functions_desc"
                newLabelKey="new_contact_function"
                create={configAPI.createContactFunction}
                update={configAPI.updateContactFunction}
              />
            </TabsContent>
            <TabsContent value="cycletimes" className="mt-6">
              <CycleTimesTab lang={lang} />
            </TabsContent>
            <TabsContent value="users" className="mt-6">
              <UsersTab
                lang={lang}
                users={users}
                setUsers={setUsers}
                roles={roles}
                currentUserId={user.id}
              />
            </TabsContent>
            <TabsContent value="roles" className="mt-6">
              <RolesTab lang={lang} roles={roles} setRoles={setRoles} />
            </TabsContent>
            <TabsContent value="pnl" className="mt-6">
              <FactoryPnLConfigPage lang={lang} />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}

// =========================================================================
// Machines
// =========================================================================
function MachinesTab({ lang, machines, setMachines }) {
  const emptyForm = {
    name: '',
    tonnage: '',
    hourly_rate_eur: '',
    max_projected_area_cm2: '',
    energy_eur_per_kg: '',
    notes: '',
  };
  const [newForm, setNewForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const create = async () => {
    try {
      const energyRaw = String(newForm.energy_eur_per_kg ?? '').trim().replace(',', '.');
      const energyNum = energyRaw === '' ? null : parseFloat(energyRaw);
      const res = await configAPI.createMachine({
        name: newForm.name,
        tonnage: parseInt(newForm.tonnage, 10),
        hourly_rate_eur: parseFloat(newForm.hourly_rate_eur),
        max_projected_area_cm2: parseFloat(newForm.max_projected_area_cm2),
        energy_eur_per_kg: Number.isFinite(energyNum) ? energyNum : null,
        notes: newForm.notes,
      });
      setMachines((prev) => [...prev, res.data]);
      setNewForm(emptyForm);
      toast.success(t('machine_created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditForm({
      name: m.name,
      tonnage: m.tonnage,
      hourly_rate_eur: m.hourly_rate_eur,
      max_projected_area_cm2: m.max_projected_area_cm2,
      energy_eur_per_kg:
        m.energy_eur_per_kg == null || m.energy_eur_per_kg === '' ? '' : String(m.energy_eur_per_kg),
      notes: m.notes || '',
      active: m.active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    try {
      const energyRaw = String(editForm.energy_eur_per_kg ?? '').trim().replace(',', '.');
      const energyNum = energyRaw === '' ? null : parseFloat(energyRaw);
      const res = await configAPI.updateMachine(id, {
        name: editForm.name,
        tonnage: parseInt(editForm.tonnage, 10),
        hourly_rate_eur: parseFloat(editForm.hourly_rate_eur),
        max_projected_area_cm2: parseFloat(editForm.max_projected_area_cm2),
        energy_eur_per_kg: Number.isFinite(energyNum) ? energyNum : null,
        notes: editForm.notes,
        active: editForm.active,
      });
      setMachines((prev) => prev.map((m) => (m.id === id ? res.data : m)));
      cancelEdit();
      toast.success(t('machine_updated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };

  const toggleActive = async (m) => {
    try {
      const res = await configAPI.updateMachine(m.id, {
        name: m.name,
        tonnage: m.tonnage,
        hourly_rate_eur: m.hourly_rate_eur,
        max_projected_area_cm2: m.max_projected_area_cm2,
        energy_eur_per_kg: m.energy_eur_per_kg ?? null,
        notes: m.notes || '',
        active: !m.active,
      });
      setMachines((prev) => prev.map((x) => (x.id === m.id ? res.data : x)));
      toast.success(m.active ? t('machine_deactivated', lang) : t('machine_activated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    }
  };

  const setNew = (k) => (e) =>
    setNewForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));
  const setEdit = (k) => (e) =>
    setEditForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t('new_machine', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label>{t('name', lang)}</Label>
              <Input value={newForm.name} onChange={setNew('name')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('tonnage', lang)}</Label>
              <Input type="number" value={newForm.tonnage} onChange={setNew('tonnage')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('hourly_rate', lang)}</Label>
              <Input type="number" step={0.01} value={newForm.hourly_rate_eur} onChange={setNew('hourly_rate_eur')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('max_area', lang)}</Label>
              <Input type="number" value={newForm.max_projected_area_cm2} onChange={setNew('max_projected_area_cm2')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('machine_energy_eur_per_kg', lang)}</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={newForm.energy_eur_per_kg}
                onChange={setNew('energy_eur_per_kg')}
                placeholder="0.40"
              />
            </div>
          </div>
          <Button onClick={create}>
            <Plus className="h-4 w-4" /> {t('create', lang)}
          </Button>
        </CardContent>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('name', lang)}</TableHead>
              <TableHead>{t('tonnage', lang)}</TableHead>
              <TableHead>{t('hourly_rate', lang)}</TableHead>
              <TableHead>{t('max_area_header', lang)}</TableHead>
              <TableHead>{t('machine_energy_eur_per_kg', lang)}</TableHead>
              <TableHead>{t('active', lang)}</TableHead>
              <TableHead className="w-[220px]">{t('actions', lang)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {machines.map((m) =>
              editingId === m.id ? (
                <TableRow key={m.id}>
                  <TableCell>
                    <Input value={editForm.name} onChange={setEdit('name')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" value={editForm.tonnage} onChange={setEdit('tonnage')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={0.01} value={editForm.hourly_rate_eur} onChange={setEdit('hourly_rate_eur')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" value={editForm.max_projected_area_cm2} onChange={setEdit('max_projected_area_cm2')} />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={editForm.energy_eur_per_kg}
                      onChange={setEdit('energy_eur_per_kg')}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={!!editForm.active}
                      onCheckedChange={(v) => setEditForm((p) => ({ ...p, active: Boolean(v) }))}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="success" onClick={() => saveEdit(m.id)}>
                        <Check className="h-3.5 w-3.5" /> {t('save', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>
                        {t('cancel', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={m.id} className={!m.active ? 'opacity-60' : ''}>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell className="tnum">{m.tonnage}T</TableCell>
                  <TableCell className="tnum">€{m.hourly_rate_eur}</TableCell>
                  <TableCell className="tnum">{m.max_projected_area_cm2}</TableCell>
                  <TableCell className="tnum text-xs">
                    {m.energy_eur_per_kg != null && m.energy_eur_per_kg !== '' ? m.energy_eur_per_kg : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={m.active ? 'success' : 'muted'}>
                      {m.active ? t('active', lang) : t('inactive', lang)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(m)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(m)}>
                        <Power className="h-3.5 w-3.5" /> {m.active ? t('deactivate', lang) : t('activate', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// =========================================================================
// Alloys
// =========================================================================
function AlloysTab({ lang, alloys, setAlloys }) {
  const emptyForm = {
    code: '',
    description: '',
    base_mb_eur_per_t: '',
    premium_eur_per_t: '',
    premium_sell_eur_per_t: '',
    default_paf_percent: '5',
  };
  const [newForm, setNewForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  // Histórico de versões de uma liga (diálogo).
  const [historyAlloy, setHistoryAlloy] = useState(null);
  const [historyVersions, setHistoryVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (a) => {
    setHistoryAlloy(a);
    setHistoryLoading(true);
    setHistoryVersions([]);
    try {
      const res = await configAPI.getAlloyVersions(a.id);
      setHistoryVersions(res.data || []);
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    } finally {
      setHistoryLoading(false);
    }
  };

  // Ambos os prémios são obrigatórios: o de venda entra no cálculo do VM,
  // o de compra é registo obrigatório da liga. Sem validação, o "|| 0"
  // silencioso mascararia campos por preencher.
  const premiumsInvalid = (form) =>
    !Number.isFinite(parseFloat(form.premium_eur_per_t))
    || !Number.isFinite(parseFloat(form.premium_sell_eur_per_t));

  const create = async () => {
    if (premiumsInvalid(newForm)) {
      toast.error(t('alloy_premiums_required', lang));
      return;
    }
    try {
      const pafPct = parseFloat(newForm.default_paf_percent);
      const defaultPafDecimal = Number.isFinite(pafPct) ? pafPct / 100 : 0.05;
      const res = await configAPI.createAlloy({
        code: newForm.code,
        description: newForm.description,
        base_mb_eur_per_t: parseFloat(newForm.base_mb_eur_per_t),
        premium_eur_per_t: parseFloat(newForm.premium_eur_per_t),
        premium_sell_eur_per_t: parseFloat(newForm.premium_sell_eur_per_t),
        default_paf_percent: defaultPafDecimal,
      });
      setAlloys((prev) => [...prev, res.data]);
      setNewForm(emptyForm);
      toast.success(t('alloy_created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditForm({
      code: a.code,
      description: a.description || '',
      base_mb_eur_per_t: a.base_mb_eur_per_t,
      premium_eur_per_t: a.premium_eur_per_t,
      premium_sell_eur_per_t: a.premium_sell_eur_per_t ?? 0,
      default_paf_percent:
        a.default_paf_percent == null ? '' : String(Number((a.default_paf_percent * 100).toFixed(4))),
      active: a.active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    if (premiumsInvalid(editForm)) {
      toast.error(t('alloy_premiums_required', lang));
      return;
    }
    try {
      const pafPct = parseFloat(editForm.default_paf_percent);
      const defaultPafDecimal = Number.isFinite(pafPct) ? pafPct / 100 : 0.05;
      const res = await configAPI.updateAlloy(id, {
        code: editForm.code,
        description: editForm.description,
        base_mb_eur_per_t: parseFloat(editForm.base_mb_eur_per_t),
        premium_eur_per_t: parseFloat(editForm.premium_eur_per_t),
        premium_sell_eur_per_t: parseFloat(editForm.premium_sell_eur_per_t),
        default_paf_percent: defaultPafDecimal,
        active: editForm.active,
      });
      setAlloys((prev) => prev.map((a) => (a.id === id ? res.data : a)));
      cancelEdit();
      toast.success(t('alloy_updated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };

  const toggleActive = async (a) => {
    try {
      const res = await configAPI.updateAlloy(a.id, {
        code: a.code,
        description: a.description || '',
        base_mb_eur_per_t: a.base_mb_eur_per_t,
        premium_eur_per_t: a.premium_eur_per_t,
        premium_sell_eur_per_t: a.premium_sell_eur_per_t ?? 0,
        default_paf_percent: a.default_paf_percent,
        active: !a.active,
      });
      setAlloys((prev) => prev.map((x) => (x.id === a.id ? res.data : x)));
      toast.success(a.active ? t('alloy_deactivated', lang) : t('alloy_activated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    }
  };

  const setNew = (k) => (e) =>
    setNewForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));
  const setEdit = (k) => (e) =>
    setEditForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t('new_alloy', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            <div className="space-y-1.5">
              <Label>{t('code', lang)}</Label>
              <Input value={newForm.code} onChange={setNew('code')} placeholder="EN AC-47100" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('description', lang)}</Label>
              <Input value={newForm.description} onChange={setNew('description')} placeholder="AlSi12Cu1(Fe)" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('base_mb', lang)}</Label>
              <Input type="number" step={0.01} value={newForm.base_mb_eur_per_t} onChange={setNew('base_mb_eur_per_t')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('premium_buy', lang)}</Label>
              <Input type="number" step={0.01} value={newForm.premium_eur_per_t} onChange={setNew('premium_eur_per_t')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('premium_sell', lang)}</Label>
              <Input type="number" step={0.01} value={newForm.premium_sell_eur_per_t} onChange={setNew('premium_sell_eur_per_t')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('paf_hint', lang)} (%)</Label>
              <Input type="number" step={0.1} value={newForm.default_paf_percent} onChange={setNew('default_paf_percent')} />
            </div>
          </div>
          <Button onClick={create}>
            <Plus className="h-4 w-4" /> {t('create', lang)}
          </Button>
        </CardContent>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('code', lang)}</TableHead>
              <TableHead>{t('description', lang)}</TableHead>
              <TableHead>{t('base_mb', lang)}</TableHead>
              <TableHead>{t('premium_buy', lang)}</TableHead>
              <TableHead>{t('premium_sell', lang)}</TableHead>
              <TableHead>{t('paf', lang)}</TableHead>
              <TableHead>{t('active', lang)}</TableHead>
              <TableHead className="w-[220px]">{t('actions', lang)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {alloys.map((a) =>
              editingId === a.id ? (
                <TableRow key={a.id}>
                  <TableCell>
                    <Input value={editForm.code} onChange={setEdit('code')} />
                  </TableCell>
                  <TableCell>
                    <Input value={editForm.description} onChange={setEdit('description')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={0.01} value={editForm.base_mb_eur_per_t} onChange={setEdit('base_mb_eur_per_t')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={0.01} value={editForm.premium_eur_per_t} onChange={setEdit('premium_eur_per_t')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={0.01} value={editForm.premium_sell_eur_per_t} onChange={setEdit('premium_sell_eur_per_t')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={0.1} value={editForm.default_paf_percent} onChange={setEdit('default_paf_percent')} />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={!!editForm.active}
                      onCheckedChange={(v) => setEditForm((p) => ({ ...p, active: Boolean(v) }))}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="success" onClick={() => saveEdit(a.id)}>
                        <Check className="h-3.5 w-3.5" /> {t('save', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>
                        {t('cancel', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={a.id} className={!a.active ? 'opacity-60' : ''}>
                  <TableCell className="font-medium">{a.code}</TableCell>
                  <TableCell>{a.description}</TableCell>
                  <TableCell className="tnum">€{a.base_mb_eur_per_t}</TableCell>
                  <TableCell className="tnum">€{a.premium_eur_per_t}</TableCell>
                  <TableCell className="tnum">€{a.premium_sell_eur_per_t ?? 0}</TableCell>
                  <TableCell className="tnum">{(a.default_paf_percent * 100).toFixed(0)}%</TableCell>
                  <TableCell>
                    <Badge variant={a.active ? 'success' : 'muted'}>{a.active ? t('active', lang) : t('inactive', lang)}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(a)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openHistory(a)}>
                        <History className="h-3.5 w-3.5" /> {t('history', lang)}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(a)}>
                        <Power className="h-3.5 w-3.5" /> {a.active ? t('deactivate', lang) : t('activate', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!historyAlloy} onOpenChange={(o) => { if (!o) setHistoryAlloy(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {t('alloy_history_title', lang)}{historyAlloy ? ` — ${historyAlloy.code}` : ''}
            </DialogTitle>
            <DialogDescription>{t('alloy_history_hint', lang)}</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">{t('loading', lang)}</div>
          ) : historyVersions.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">{t('no_data', lang)}</div>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('alloy_version', lang)}</TableHead>
                    <TableHead>{t('effective_date', lang)}</TableHead>
                    <TableHead>{t('base_mb', lang)}</TableHead>
                    <TableHead>{t('premium_buy', lang)}</TableHead>
                    <TableHead>{t('premium_sell', lang)}</TableHead>
                    <TableHead>{t('paf', lang)}</TableHead>
                    <TableHead>{t('recorded_by', lang)}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...historyVersions].reverse().map((v) => (
                    <TableRow key={v.id}>
                      <TableCell className="font-medium">v{v.version_number}</TableCell>
                      <TableCell>{v.effective_date ? String(v.effective_date).slice(0, 10) : '—'}</TableCell>
                      <TableCell className="tnum">€{v.base_mb_eur_per_t}</TableCell>
                      <TableCell className="tnum">€{v.premium_eur_per_t}</TableCell>
                      <TableCell className="tnum">€{v.premium_sell_eur_per_t ?? 0}</TableCell>
                      <TableCell className="tnum">{((v.default_paf_percent ?? 0) * 100).toFixed(0)}%</TableCell>
                      <TableCell>{v.created_by_name || t('system_author', lang)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHistoryAlloy(null)}>{t('close', lang)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================================
// Finishing Operations (configuráveis pelo gestor)
// =========================================================================
function FinishingTab({ lang, rates, setRates }) {
  const emptyForm = {
    operation_name: '',
    default_rate_eur_hr: '',
    parallel_operators: '1',
    category: 'finishing',
  };
  const [newForm, setNewForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  // Filtro de listagem por categoria — 'all' | 'finishing' | 'extra_works'.
  // Default 'all' para o admin ver tudo num único ecrã.
  const [filterCategory, setFilterCategory] = useState('all');

  // Cadência (pçs/h) e Rejeição (%) são preenchidas pelo comercial em cada
  // cotação, não fazem parte da configuração da fábrica. Mantemos as colunas
  // na DB com valores neutros (null/0) para retrocompatibilidade.
  const toPayload = (form, active = true) => {
    const par = parseInt(String(form.parallel_operators ?? '1'), 10);
    return {
      operation_name: (form.operation_name || '').trim(),
      default_rate_eur_hr: parseFloat(form.default_rate_eur_hr) || 0,
      default_cadence_pcs_hr: null,
      default_scrap_percent: 0,
      parallel_operators: Number.isFinite(par) && par >= 1 ? Math.min(par, 20) : 1,
      active,
      category: form.category || 'finishing',
    };
  };

  const create = async () => {
    if (!newForm.operation_name.trim()) {
      toast.error(t('finishing_name_required', lang));
      return;
    }
    try {
      const res = await configAPI.createFinishingRate(toPayload(newForm, true));
      setRates((prev) => [...prev, res.data].sort((a, b) =>
        a.operation_name.localeCompare(b.operation_name)
      ));
      setNewForm(emptyForm);
      toast.success(t('finishing_rate_created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      operation_name: r.operation_name,
      default_rate_eur_hr: r.default_rate_eur_hr,
      parallel_operators: String(r.parallel_operators ?? 1),
      active: r.active,
      category: r.category || 'finishing',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    try {
      const res = await configAPI.updateFinishingRate(id, toPayload(editForm, !!editForm.active));
      setRates((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      cancelEdit();
      toast.success(t('finishing_rate_updated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };

  const toggleActive = async (r) => {
    try {
      const res = await configAPI.updateFinishingRate(r.id, {
        operation_name: r.operation_name,
        default_rate_eur_hr: r.default_rate_eur_hr,
        default_cadence_pcs_hr: null,
        default_scrap_percent: 0,
        parallel_operators: Math.max(1, Math.min(20, parseInt(String(r.parallel_operators ?? 1), 10) || 1)),
        active: !r.active,
        category: r.category || 'finishing',
      });
      setRates((prev) => prev.map((x) => (x.id === r.id ? res.data : x)));
      toast.success(
        r.active ? t('finishing_rate_deactivated', lang) : t('finishing_rate_activated', lang)
      );
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    }
  };

  const setNew = (k) => (e) =>
    setNewForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));
  const setEdit = (k) => (e) =>
    setEditForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));

  const visibleRates = rates.filter(
    (r) => filterCategory === 'all' || (r.category || 'finishing') === filterCategory
  );
  const categoryLabel = (cat) =>
    cat === 'extra_works' ? t('category_extra_works', lang) : t('category_finishing', lang);

  return (
    <div className="space-y-5">
      <Alert>
        <AlertTitle>{t('finishing_admin_title', lang)}</AlertTitle>
        <AlertDescription>{t('finishing_admin_desc', lang)}</AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>{t('new_finishing_rate', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label>{t('finishing_op_name', lang)}</Label>
              <Input value={newForm.operation_name} onChange={setNew('operation_name')} placeholder="Granalagem" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('finishing_rate_hdr', lang)}</Label>
              <Input
                type="number"
                step={0.01}
                value={newForm.default_rate_eur_hr}
                onChange={setNew('default_rate_eur_hr')}
                placeholder="35"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('finishing_parallel_ops', lang)}</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={newForm.parallel_operators}
                onChange={setNew('parallel_operators')}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('finishing_admin_category', lang)}</Label>
              <Select value={newForm.category} onValueChange={(v) => setNewForm((p) => ({ ...p, category: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="finishing">{t('category_finishing', lang)}</SelectItem>
                  <SelectItem value="extra_works">{t('category_extra_works', lang)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={create}>
            <Plus className="h-4 w-4" /> {t('create', lang)}
          </Button>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground">{t('finishing_admin_category', lang)}</Label>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all', lang)}</SelectItem>
            <SelectItem value="finishing">{t('category_finishing', lang)}</SelectItem>
            <SelectItem value="extra_works">{t('category_extra_works', lang)}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('finishing_op_name', lang)}</TableHead>
              <TableHead>{t('finishing_rate_hdr', lang)}</TableHead>
              <TableHead>{t('finishing_parallel_ops', lang)}</TableHead>
              <TableHead>{t('finishing_admin_category', lang)}</TableHead>
              <TableHead>{t('active', lang)}</TableHead>
              <TableHead className="w-[220px]">{t('actions', lang)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                  {t('no_finishing_rates', lang)}
                </TableCell>
              </TableRow>
            ) : (
              visibleRates.map((r) =>
                editingId === r.id ? (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Input value={editForm.operation_name} onChange={setEdit('operation_name')} />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step={0.01}
                        value={editForm.default_rate_eur_hr}
                        onChange={setEdit('default_rate_eur_hr')}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={1}
                        max={20}
                        value={editForm.parallel_operators}
                        onChange={setEdit('parallel_operators')}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={editForm.category || 'finishing'}
                        onValueChange={(v) => setEditForm((p) => ({ ...p, category: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="finishing">{t('category_finishing', lang)}</SelectItem>
                          <SelectItem value="extra_works">{t('category_extra_works', lang)}</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={!!editForm.active}
                        onCheckedChange={(v) => setEditForm((p) => ({ ...p, active: Boolean(v) }))}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="success" onClick={() => saveEdit(r.id)}>
                          <Check className="h-3.5 w-3.5" /> {t('save', lang)}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit}>
                          {t('cancel', lang)}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={r.id} className={!r.active ? 'opacity-60' : ''}>
                    <TableCell className="font-medium">{r.operation_name}</TableCell>
                    <TableCell className="tnum">€{Number(r.default_rate_eur_hr).toFixed(2)}/h</TableCell>
                    <TableCell className="tnum tabular-nums">{r.parallel_operators ?? 1}</TableCell>
                    <TableCell>
                      <Badge variant={r.category === 'extra_works' ? 'info' : 'muted'}>
                        {categoryLabel(r.category)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.active ? 'success' : 'muted'}>
                        {r.active ? t('active', lang) : t('inactive', lang)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(r)}>
                          <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleActive(r)}>
                          <Power className="h-3.5 w-3.5" />{' '}
                          {r.active ? t('deactivate', lang) : t('activate', lang)}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              )
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// =========================================================================
// Box Types (Caixas — catálogo usado no card Embalagem da cotação)
// =========================================================================
function BoxTypesTab({ lang, boxTypes, setBoxTypes }) {
  const [palletCost, setPalletCost] = useState('');
  const [palletCostSaving, setPalletCostSaving] = useState(false);

  useEffect(() => {
    configAPI.getPalletCost()
      .then((res) => setPalletCost(String(res.data?.pallet_cost_eur ?? 0)))
      .catch(() => setPalletCost('0'));
  }, []);

  const savePalletCost = async () => {
    const value = parseFloat(palletCost);
    if (!Number.isFinite(value) || value < 0) {
      toast.error(t('pallet_cost_invalid', lang));
      return;
    }
    setPalletCostSaving(true);
    try {
      const res = await configAPI.updatePalletCost({ pallet_cost_eur: value });
      setPalletCost(String(res.data.pallet_cost_eur));
      toast.success(t('pallet_cost_saved', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    } finally {
      setPalletCostSaving(false);
    }
  };

  const emptyForm = {
    name: '',
    description: '',
    length_mm: '',
    width_mm: '',
    height_mm: '',
    price_eur: '',
    volume_l: '',
  };
  const [newForm, setNewForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Volume útil deriva de L×W×H (mm³ → litros) quando o utilizador deixa em
  // branco. Conversão: 1 L = 1 000 cm³ = 1 000 000 mm³.
  const deriveVolumeL = (length, width, height) => {
    const L = parseFloat(length);
    const W = parseFloat(width);
    const H = parseFloat(height);
    if (!Number.isFinite(L) || !Number.isFinite(W) || !Number.isFinite(H)) return null;
    return Number(((L * W * H) / 1_000_000).toFixed(4));
  };

  const toPayload = (form, active = true) => {
    const length = parseFloat(form.length_mm) || 0;
    const width = parseFloat(form.width_mm) || 0;
    const height = parseFloat(form.height_mm) || 0;
    const price = parseFloat(form.price_eur) || 0;
    const rawVol = (form.volume_l ?? '').toString().trim();
    let volume = rawVol === '' ? null : parseFloat(rawVol);
    if (volume === null || !Number.isFinite(volume)) {
      volume = deriveVolumeL(length, width, height);
    }
    return {
      name: (form.name || '').trim(),
      description: (form.description || '').trim(),
      length_mm: length,
      width_mm: width,
      height_mm: height,
      price_eur: price,
      volume_l: volume,
      active,
    };
  };

  const create = async () => {
    if (!newForm.name.trim()) {
      toast.error(t('name_required_toast', lang));
      return;
    }
    try {
      const res = await configAPI.createBoxType(toPayload(newForm, true));
      setBoxTypes((prev) =>
        [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name))
      );
      setNewForm(emptyForm);
      toast.success(t('box_type_created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setEditForm({
      name: b.name,
      description: b.description || '',
      length_mm: b.length_mm,
      width_mm: b.width_mm,
      height_mm: b.height_mm,
      price_eur: b.price_eur,
      volume_l: b.volume_l ?? '',
      active: b.active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    try {
      const res = await configAPI.updateBoxType(id, toPayload(editForm, !!editForm.active));
      setBoxTypes((prev) => prev.map((b) => (b.id === id ? res.data : b)));
      cancelEdit();
      toast.success(t('box_type_updated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };

  const toggleActive = async (b) => {
    try {
      const res = await configAPI.updateBoxType(b.id, {
        name: b.name,
        description: b.description || '',
        length_mm: b.length_mm,
        width_mm: b.width_mm,
        height_mm: b.height_mm,
        price_eur: b.price_eur,
        volume_l: b.volume_l,
        active: !b.active,
      });
      setBoxTypes((prev) => prev.map((x) => (x.id === b.id ? res.data : x)));
      toast.success(b.active ? t('box_type_deactivated', lang) : t('box_type_activated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    }
  };

  const setNew = (k) => (e) =>
    setNewForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));
  const setEdit = (k) => (e) =>
    setEditForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));

  return (
    <div className="space-y-5">
      <Alert>
        <AlertTitle>{t('boxes_admin_title', lang)}</AlertTitle>
        <AlertDescription>{t('boxes_admin_desc', lang)}</AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>{t('new_box_type', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label>{t('box_type_name', lang)}</Label>
              <Input value={newForm.name} onChange={setNew('name')} placeholder="Caixa 600×400×300" />
            </div>
            <div className="space-y-1.5 md:col-span-2 lg:col-span-3">
              <Label>{t('box_type_description', lang)}</Label>
              <Input value={newForm.description} onChange={setNew('description')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('box_type_length_mm', lang)}</Label>
              <Input type="number" step={1} value={newForm.length_mm} onChange={setNew('length_mm')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('box_type_width_mm', lang)}</Label>
              <Input type="number" step={1} value={newForm.width_mm} onChange={setNew('width_mm')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('box_type_height_mm', lang)}</Label>
              <Input type="number" step={1} value={newForm.height_mm} onChange={setNew('height_mm')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('box_type_price_eur', lang)}</Label>
              <Input type="number" step={0.01} value={newForm.price_eur} onChange={setNew('price_eur')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('box_type_volume_l', lang)}</Label>
              <Input
                type="number"
                step={0.01}
                value={newForm.volume_l}
                onChange={setNew('volume_l')}
                placeholder={String(deriveVolumeL(newForm.length_mm, newForm.width_mm, newForm.height_mm) ?? '')}
              />
              <p className="text-xs text-muted-foreground">{t('box_type_volume_hint', lang)}</p>
            </div>
          </div>
          <Button onClick={create}>
            <Plus className="h-4 w-4" /> {t('create', lang)}
          </Button>
        </CardContent>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('box_type_name', lang)}</TableHead>
              <TableHead>{t('box_type_description', lang)}</TableHead>
              <TableHead className="text-right">{t('box_type_length_mm', lang)}</TableHead>
              <TableHead className="text-right">{t('box_type_width_mm', lang)}</TableHead>
              <TableHead className="text-right">{t('box_type_height_mm', lang)}</TableHead>
              <TableHead className="text-right">{t('box_type_volume_l', lang)}</TableHead>
              <TableHead className="text-right">{t('box_type_price_eur', lang)}</TableHead>
              <TableHead>{t('active', lang)}</TableHead>
              <TableHead className="w-[220px]">{t('actions', lang)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {boxTypes.map((b) =>
              editingId === b.id ? (
                <TableRow key={b.id}>
                  <TableCell>
                    <Input value={editForm.name} onChange={setEdit('name')} />
                  </TableCell>
                  <TableCell>
                    <Input value={editForm.description} onChange={setEdit('description')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={1} value={editForm.length_mm} onChange={setEdit('length_mm')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={1} value={editForm.width_mm} onChange={setEdit('width_mm')} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={1} value={editForm.height_mm} onChange={setEdit('height_mm')} />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step={0.01}
                      value={editForm.volume_l}
                      onChange={setEdit('volume_l')}
                      placeholder={String(
                        deriveVolumeL(editForm.length_mm, editForm.width_mm, editForm.height_mm) ?? ''
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <Input type="number" step={0.01} value={editForm.price_eur} onChange={setEdit('price_eur')} />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={!!editForm.active}
                      onCheckedChange={(v) => setEditForm((p) => ({ ...p, active: Boolean(v) }))}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="success" onClick={() => saveEdit(b.id)}>
                        <Check className="h-3.5 w-3.5" /> {t('save', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>
                        {t('cancel', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={b.id} className={!b.active ? 'opacity-60' : ''}>
                  <TableCell className="font-medium">{b.name}</TableCell>
                  <TableCell className="max-w-[320px] truncate" title={b.description || ''}>
                    {b.description || '—'}
                  </TableCell>
                  <TableCell className="text-right tnum">{b.length_mm}</TableCell>
                  <TableCell className="text-right tnum">{b.width_mm}</TableCell>
                  <TableCell className="text-right tnum">{b.height_mm}</TableCell>
                  <TableCell className="text-right tnum">
                    {b.volume_l != null ? Number(b.volume_l).toFixed(2) : '—'}
                  </TableCell>
                  <TableCell className="text-right tnum">€{Number(b.price_eur).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant={b.active ? 'success' : 'muted'}>
                      {b.active ? t('active', lang) : t('inactive', lang)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(b)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(b)}>
                        <Power className="h-3.5 w-3.5" />{' '}
                        {b.active ? t('deactivate', lang) : t('activate', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('pallet_cost_admin_title', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('pallet_cost_admin_desc', lang)}</p>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="admin-pallet-cost">{t('pallet_cost', lang)}</Label>
              <Input
                id="admin-pallet-cost"
                type="number"
                step={0.01}
                min={0}
                value={palletCost}
                onChange={(e) => setPalletCost(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={savePalletCost} disabled={palletCostSaving}>
            <Check className="h-4 w-4" /> {t('save', lang)}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// =========================================================================
// Suppliers (Fornecedores — catálogo usado no card Subcontratação)
// =========================================================================
const SUPPLIER_TYPE_OPTIONS = [
  { value: 'machining', labelKey: 'supplier_type_machining' },
  { value: 'surface_treatment', labelKey: 'supplier_type_surface_treatment' },
  { value: 'painting', labelKey: 'supplier_type_painting' },
  { value: 'other', labelKey: 'supplier_type_other' },
];

// Catálogo simples (nome + ativo) — usado para "Setores de atividade" e
// "Funções de contacto". Recebe as funções create/update da configAPI.
function SimpleCatalogTab({ lang, items, setItems, titleKey, descKey, newLabelKey, create, update }) {
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const sortByName = (arr) => [...arr].sort((a, b) => a.name.localeCompare(b.name));

  const doCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t('name_required_toast', lang));
      return;
    }
    try {
      const res = await create({ name: trimmed, active: true });
      setItems((prev) => sortByName([...prev, res.data]));
      setName('');
      toast.success(t('created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const saveEdit = async (id) => {
    const trimmed = editName.trim();
    if (!trimmed) {
      toast.error(t('name_required_toast', lang));
      return;
    }
    try {
      const item = items.find((x) => x.id === id);
      const res = await update(id, { name: trimmed, active: item?.active ?? true });
      setItems((prev) => sortByName(prev.map((x) => (x.id === id ? res.data : x))));
      setEditingId(null);
      setEditName('');
      toast.success(t('saved', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await update(item.id, { name: item.name, active: !item.active });
      setItems((prev) => prev.map((x) => (x.id === item.id ? res.data : x)));
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    }
  };

  return (
    <div className="space-y-5">
      <Alert>
        <AlertTitle>{t(titleKey, lang)}</AlertTitle>
        <AlertDescription>{t(descKey, lang)}</AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>{t(newLabelKey, lang)}</CardTitle>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <Label>{t('name', lang)}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <Button onClick={doCreate}>
            <Plus className="h-4 w-4" /> {t('create', lang)}
          </Button>
        </CardContent>
      </Card>

      <Card className="p-0">
        {items.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('no_results', lang)}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('name', lang)}</TableHead>
                <TableHead className="w-[120px]">{t('status', lang)}</TableHead>
                <TableHead className="w-[200px]">{t('actions', lang)}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className={item.active ? '' : 'opacity-60'}>
                  <TableCell>
                    {editingId === item.id ? (
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                    ) : (
                      <span className="font-medium">{item.name}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{item.active ? t('active', lang) : t('inactive', lang)}</Badge>
                  </TableCell>
                  <TableCell>
                    {editingId === item.id ? (
                      <span className="flex gap-1">
                        <Button size="sm" onClick={() => saveEdit(item.id)}>{t('save', lang)}</Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditingId(null); setEditName(''); }}>
                          {t('cancel', lang)}
                        </Button>
                      </span>
                    ) : (
                      <span className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => { setEditingId(item.id); setEditName(item.name); }}>
                          <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleActive(item)}>
                          {item.active ? t('deactivate', lang) : t('activate', lang)}
                        </Button>
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function SuppliersTab({ lang, suppliers, setSuppliers }) {
  const emptyForm = { name: '', supplier_type: 'machining', notes: '' };
  const [newForm, setNewForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const toPayload = (form, active = true) => ({
    name: (form.name || '').trim(),
    supplier_type: form.supplier_type || 'machining',
    notes: (form.notes || '').trim(),
    active,
  });

  const create = async () => {
    if (!newForm.name.trim()) {
      toast.error(t('name_required_toast', lang));
      return;
    }
    try {
      const res = await configAPI.createSupplier(toPayload(newForm, true));
      setSuppliers((prev) =>
        [...prev, res.data].sort(
          (a, b) =>
            a.supplier_type.localeCompare(b.supplier_type) || a.name.localeCompare(b.name)
        )
      );
      setNewForm(emptyForm);
      toast.success(t('supplier_created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({
      name: s.name,
      supplier_type: s.supplier_type,
      notes: s.notes || '',
      active: s.active,
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };
  const saveEdit = async (id) => {
    try {
      const res = await configAPI.updateSupplier(id, toPayload(editForm, !!editForm.active));
      setSuppliers((prev) => prev.map((s) => (s.id === id ? res.data : s)));
      cancelEdit();
      toast.success(t('supplier_updated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };
  const toggleActive = async (s) => {
    try {
      const res = await configAPI.updateSupplier(s.id, {
        name: s.name,
        supplier_type: s.supplier_type,
        notes: s.notes || '',
        active: !s.active,
      });
      setSuppliers((prev) => prev.map((x) => (x.id === s.id ? res.data : x)));
      toast.success(s.active ? t('supplier_deactivated', lang) : t('supplier_activated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    }
  };

  const setNew = (k) => (e) =>
    setNewForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));
  const setEdit = (k) => (e) =>
    setEditForm((p) => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));

  return (
    <div className="space-y-5">
      <Alert>
        <AlertTitle>{t('suppliers_admin_title', lang)}</AlertTitle>
        <AlertDescription>{t('suppliers_admin_desc', lang)}</AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>{t('new_supplier', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label>{t('supplier_name', lang)}</Label>
              <Input value={newForm.name} onChange={setNew('name')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('supplier_type', lang)}</Label>
              <Select value={newForm.supplier_type} onValueChange={setNew('supplier_type')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPLIER_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {t(o.labelKey, lang)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('supplier_notes', lang)}</Label>
              <Input value={newForm.notes} onChange={setNew('notes')} />
            </div>
          </div>
          <Button onClick={create}>
            <Plus className="h-4 w-4" /> {t('create', lang)}
          </Button>
        </CardContent>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('supplier_name', lang)}</TableHead>
              <TableHead>{t('supplier_type', lang)}</TableHead>
              <TableHead>{t('supplier_notes', lang)}</TableHead>
              <TableHead>{t('active', lang)}</TableHead>
              <TableHead className="w-[220px]">{t('actions', lang)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {suppliers.map((s) =>
              editingId === s.id ? (
                <TableRow key={s.id}>
                  <TableCell>
                    <Input value={editForm.name} onChange={setEdit('name')} />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={editForm.supplier_type}
                      onValueChange={setEdit('supplier_type')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPLIER_TYPE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {t(o.labelKey, lang)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input value={editForm.notes} onChange={setEdit('notes')} />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={!!editForm.active}
                      onCheckedChange={(v) => setEditForm((p) => ({ ...p, active: Boolean(v) }))}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="success" onClick={() => saveEdit(s.id)}>
                        <Check className="h-3.5 w-3.5" /> {t('save', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>
                        {t('cancel', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={s.id} className={!s.active ? 'opacity-60' : ''}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell>
                    {t(`supplier_type_${s.supplier_type}`, lang) || s.supplier_type}
                  </TableCell>
                  <TableCell className="max-w-[320px] truncate" title={s.notes || ''}>
                    {s.notes || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={s.active ? 'success' : 'muted'}>
                      {s.active ? t('active', lang) : t('inactive', lang)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(s)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => toggleActive(s)}>
                        <Power className="h-3.5 w-3.5" />{' '}
                        {s.active ? t('deactivate', lang) : t('activate', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// =========================================================================
// Users
// =========================================================================
function UsersTab({ lang, users, setUsers, roles, currentUserId }) {
  const emptyForm = {
    username: '',
    full_name: '',
    email: '',
    password: '',
    role_id: '',
    is_admin: false,
    language: 'pt',
  };
  const [newForm, setNewForm] = useState(emptyForm);
  const [newErrors, setNewErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  // VAL-04: validação no frontend antes de chamar a API (espelha as
  // constraints do schema backend UserCreate: username≥3, full_name≥2,
  // password≥8, email com formato válido) para dar feedback imediato e
  // evitar 422 silenciosos.
  const validateNewUser = (form) => {
    const errors = {};
    const username = (form.username || '').trim();
    const fullName = (form.full_name || '').trim();
    const email = (form.email || '').trim();
    if (!username) errors.username = t('fill_required_fields', lang);
    else if (username.length < 3) errors.username = t('username_min_length', lang);
    if (!fullName) errors.full_name = t('fill_required_fields', lang);
    else if (fullName.length < 2) errors.full_name = t('name_min_length', lang);
    if (!email) errors.email = t('fill_required_fields', lang);
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = t('invalid_email', lang);
    if (!form.password) errors.password = t('fill_required_fields', lang);
    else if (form.password.length < 8) errors.password = t('password_min_length', lang);
    return errors;
  };

  const updateNewField = (field, value) => {
    setNewForm((p) => ({ ...p, [field]: value }));
    setNewErrors((p) => (p[field] ? { ...p, [field]: undefined } : p));
  };

  const create = async () => {
    const errors = validateNewUser(newForm);
    if (Object.keys(errors).length > 0) {
      setNewErrors(errors);
      toast.error(t('fill_required_fields', lang));
      return;
    }
    try {
      const res = await userAPI.create({
        ...newForm,
        username: newForm.username.trim(),
        full_name: newForm.full_name.trim(),
        email: newForm.email.trim(),
        role_id: newForm.role_id ? parseInt(newForm.role_id, 10) : null,
      });
      setUsers((prev) => [...prev, res.data]);
      setNewForm(emptyForm);
      setNewErrors({});
      toast.success(t('user_created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditForm({
      full_name: u.full_name,
      email: u.email,
      role_id: u.role_id ? String(u.role_id) : '',
      active: u.active,
      language: u.language || 'pt',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    try {
      const res = await userAPI.update(id, {
        full_name: editForm.full_name,
        email: editForm.email,
        role_id: editForm.role_id ? parseInt(editForm.role_id, 10) : null,
        active: editForm.active,
        language: editForm.language,
      });
      setUsers((prev) => prev.map((u) => (u.id === id ? res.data : u)));
      cancelEdit();
      toast.success(t('user_updated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };

  const deactivate = async (u) => {
    try {
      await userAPI.deactivate(u.id);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, active: false } : x)));
      toast.success(t('user_deactivated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    } finally {
      setDeactivateTarget(null);
    }
  };

  const reactivate = async (u) => {
    try {
      const res = await userAPI.update(u.id, { active: true });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? res.data : x)));
      toast.success(t('user_reactivated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('error', lang)));
    }
  };

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t('new_user', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label>{t('username', lang)}</Label>
              <Input
                value={newForm.username}
                onChange={(e) => updateNewField('username', e.target.value)}
                className={newErrors.username ? 'border-destructive' : ''}
                autoComplete="off"
              />
              {newErrors.username && <p className="text-xs text-destructive">{newErrors.username}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>{t('full_name', lang)}</Label>
              <Input
                value={newForm.full_name}
                onChange={(e) => updateNewField('full_name', e.target.value)}
                className={newErrors.full_name ? 'border-destructive' : ''}
              />
              {newErrors.full_name && <p className="text-xs text-destructive">{newErrors.full_name}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>{t('email', lang)}</Label>
              <Input
                type="email"
                value={newForm.email}
                onChange={(e) => updateNewField('email', e.target.value)}
                className={newErrors.email ? 'border-destructive' : ''}
              />
              {newErrors.email && <p className="text-xs text-destructive">{newErrors.email}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>{t('password', lang)}</Label>
              <Input
                type="password"
                value={newForm.password}
                onChange={(e) => updateNewField('password', e.target.value)}
                className={newErrors.password ? 'border-destructive' : ''}
                autoComplete="new-password"
              />
              {newErrors.password && <p className="text-xs text-destructive">{newErrors.password}</p>}
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label>{t('role', lang)}</Label>
              <Select
                value={newForm.role_id ? String(newForm.role_id) : 'none'}
                onValueChange={(v) => setNewForm((p) => ({ ...p, role_id: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('no_profile', lang)}</SelectItem>
                  {roles.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('language', lang)}</Label>
              <Select
                value={newForm.language}
                onValueChange={(v) => setNewForm((p) => ({ ...p, language: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pt">{t('portuguese_full', lang)}</SelectItem>
                  <SelectItem value="fr">{t('french_full', lang)}</SelectItem>
                  <SelectItem value="en">{t('english_full', lang)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <label className="inline-flex items-center gap-2 text-sm font-medium">
                <Checkbox
                  checked={newForm.is_admin}
                  onCheckedChange={(v) => setNewForm((p) => ({ ...p, is_admin: Boolean(v) }))}
                />
                {t('administrator', lang)}
              </label>
            </div>
          </div>
          <Button onClick={create}>
            <Plus className="h-4 w-4" /> {t('create_user', lang)}
          </Button>
        </CardContent>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('username', lang)}</TableHead>
              <TableHead>{t('name', lang)}</TableHead>
              <TableHead>{t('email', lang)}</TableHead>
              <TableHead>{t('role', lang)}</TableHead>
              <TableHead>{t('language', lang)}</TableHead>
              <TableHead>{t('admin', lang)}</TableHead>
              <TableHead>{t('active_m', lang)}</TableHead>
              <TableHead className="w-[220px]">{t('actions', lang)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) =>
              editingId === u.id ? (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.username}</TableCell>
                  <TableCell>
                    <Input value={editForm.full_name} onChange={(e) => setEditForm((p) => ({ ...p, full_name: e.target.value }))} />
                  </TableCell>
                  <TableCell>
                    <Input type="email" value={editForm.email} onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))} />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={editForm.role_id || 'none'}
                      onValueChange={(v) => setEditForm((p) => ({ ...p, role_id: v === 'none' ? '' : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {roles.map((r) => (
                          <SelectItem key={r.id} value={String(r.id)}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select value={editForm.language} onValueChange={(v) => setEditForm((p) => ({ ...p, language: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pt">PT</SelectItem>
                        <SelectItem value="fr">FR</SelectItem>
                        <SelectItem value="en">EN</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>{u.is_admin ? <Check className="h-4 w-4 text-success" /> : null}</TableCell>
                  <TableCell>
                    <Checkbox checked={!!editForm.active} onCheckedChange={(v) => setEditForm((p) => ({ ...p, active: Boolean(v) }))} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="success" onClick={() => saveEdit(u.id)}>
                        <Check className="h-3.5 w-3.5" /> {t('save', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>
                        {t('cancel', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={u.id} className={!u.active ? 'opacity-60' : ''}>
                  <TableCell className="font-medium">{u.username}</TableCell>
                  <TableCell>{u.full_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{roles.find((r) => r.id === u.role_id)?.name || '—'}</TableCell>
                  <TableCell>{(u.language || 'pt').toUpperCase()}</TableCell>
                  <TableCell>{u.is_admin ? <Check className="h-4 w-4 text-success" /> : null}</TableCell>
                  <TableCell>
                    <Badge variant={u.active ? 'success' : 'muted'}>{u.active ? t('active_m', lang) : t('inactive_m', lang)}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(u)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                      </Button>
                      {u.active ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={u.id === currentUserId}
                          onClick={() => setDeactivateTarget(u)}
                        >
                          <Power className="h-3.5 w-3.5" /> {t('deactivate', lang)}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => reactivate(u)}>
                          <Power className="h-3.5 w-3.5" /> {t('activate', lang)}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!deactivateTarget} onOpenChange={(open) => !open && setDeactivateTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deactivate_user_title', lang)}</DialogTitle>
            <DialogDescription>
              {t('deactivate_user_desc_prefix', lang)}{' '}
              <span className="font-mono">{deactivateTarget?.username}</span>{' '}
              {t('deactivate_user_desc_suffix', lang)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)}>
              {t('cancel', lang)}
            </Button>
            <Button variant="destructive" onClick={() => deactivate(deactivateTarget)}>
              {t('deactivate', lang)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =========================================================================
// Roles
// =========================================================================
function RolesTab({ lang, roles, setRoles }) {
  const emptyForm = { name: '', description: '', color: '#6B7280', permissions: {} };
  const [newForm, setNewForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const create = async () => {
    try {
      const res = await roleAPI.create({ ...newForm });
      setRoles((prev) => [...prev, res.data]);
      setNewForm(emptyForm);
      toast.success(t('role_created', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('create_error', lang)));
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditForm({
      name: r.name,
      description: r.description || '',
      color: r.color || '#6B7280',
      permissions: { ...(r.permissions || {}) },
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async (id) => {
    try {
      const res = await roleAPI.update(id, {
        name: editForm.name,
        description: editForm.description,
        color: editForm.color,
        permissions: editForm.permissions,
      });
      setRoles((prev) => prev.map((r) => (r.id === id ? res.data : r)));
      cancelEdit();
      toast.success(t('role_updated', lang));
    } catch (err) {
      toast.error(errorMessage(err, t('save_error', lang)));
    }
  };

  const togglePerm = (form, setForm, key) => {
    setForm((p) => ({ ...p, permissions: { ...p.permissions, [key]: !p.permissions?.[key] } }));
  };

  const PermissionEditor = ({ form, setForm }) => (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs md:grid-cols-3">
      {PERMISSION_KEYS.map((k) => (
        <div key={k} className="inline-flex min-w-0 items-center gap-1.5">
          <label className="inline-flex min-w-0 cursor-pointer items-center gap-2 font-mono">
            <Checkbox
              checked={!!form.permissions?.[k]}
              onCheckedChange={() => togglePerm(form, setForm, k)}
            />
            <span className="truncate">{k}</span>
          </label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                aria-label={t('permission_help_aria', lang)}
              >
                <Info className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs font-sans normal-case">
              {t(`perm_help_${k}`, lang)}
            </TooltipContent>
          </Tooltip>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t('new_role', lang)}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1.5 md:col-span-1">
              <Label>{t('name', lang)}</Label>
              <Input value={newForm.name} onChange={(e) => setNewForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="space-y-1.5 md:col-span-1">
              <Label>{t('description', lang)}</Label>
              <Input value={newForm.description} onChange={(e) => setNewForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('color', lang)}</Label>
              <input
                type="color"
                value={newForm.color}
                onChange={(e) => setNewForm((p) => ({ ...p, color: e.target.value }))}
                className="h-9 w-20 cursor-pointer rounded-md border border-input bg-background"
              />
            </div>
          </div>
          <div>
            <Label className="mb-2 block">{t('permissions', lang)}</Label>
            <PermissionEditor form={newForm} setForm={setNewForm} />
          </div>
          <Button onClick={create}>
            <Plus className="h-4 w-4" /> {t('create_role', lang)}
          </Button>
        </CardContent>
      </Card>

      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('name', lang)}</TableHead>
              <TableHead>{t('description', lang)}</TableHead>
              <TableHead>{t('system', lang)}</TableHead>
              <TableHead>{t('permissions', lang)}</TableHead>
              <TableHead className="w-[220px]">{t('actions', lang)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {roles.map((r) =>
              editingId === r.id ? (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={editForm.color}
                        onChange={(e) => setEditForm((p) => ({ ...p, color: e.target.value }))}
                        className="h-7 w-7 cursor-pointer rounded border border-input bg-background"
                      />
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        disabled={r.is_system}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Input value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))} />
                  </TableCell>
                  <TableCell>{r.is_system ? <Check className="h-4 w-4 text-muted-foreground" /> : null}</TableCell>
                  <TableCell>
                    <PermissionEditor form={editForm} setForm={setEditForm} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="success" onClick={() => saveEdit(r.id)}>
                        <Check className="h-3.5 w-3.5" /> {t('save', lang)}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>
                        {t('cancel', lang)}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={r.id}>
                  <TableCell>
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-3 w-3 rounded-full border border-border"
                        style={{ background: r.color }}
                        aria-hidden="true"
                      />
                      <span className="font-medium">{r.name}</span>
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{r.description || '—'}</TableCell>
                  <TableCell>
                    {r.is_system ? (
                      <Badge variant="outline" className="gap-1">
                        <Lock className="h-3 w-3" /> {t('system', lang)}
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell className="max-w-md text-xs text-muted-foreground">
                    {Object.keys(r.permissions || {})
                      .filter((k) => r.permissions[k])
                      .join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    {r.is_system ? (
                      <span className="text-xs text-muted-foreground">{t('system_role_locked', lang)}</span>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => startEdit(r)}>
                        <Pencil className="h-3.5 w-3.5" /> {t('edit', lang)}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
